import { Box, Typography } from "@mui/material";
import { Avatar } from "@/components/elements/Avatar";
import { findLabelByValue } from "@/utils/object";
import { IconAssignment } from "@/assets/icons";
import { dayjs } from "@/utils/dayjs";
import { DATE_FORMAT } from "@/constants/datetime";
import { TTalentForm } from "@/features/talent/types/talentForm";
import { useEnum } from "@/api/services/global/enum";
import { FCC } from "@/types/common";
import { text } from "@/theme/colors";
import { TalentStatusTag } from "@/features/talent/components/talent-common/TalentStatusTag";
import { ERecruitmentStatus } from "@/types/enum";
import { EMPTY_STR } from "@/constants/string";
import { TALENT_WIDTH_FULL } from "@/features/talent/constants/width";
import { Button } from "@/components/elements/Button";
import { generateResumeExcel } from "@/features/talent/utils/generateResumeExcel";
import { useGetLanguageTestList, useGetNationalityList, useGetSpecificSkillCertificateList } from "@/api/services/global/master";
import { showError } from "@/utils/error";

type TTalentProfileHeaderProps = {
  defaultValues?: Partial<TTalentForm>;
  detailPage?: boolean;
};

export const TalentProfileHeader: FCC<TTalentProfileHeaderProps> = ({ defaultValues, detailPage = false }) => {
  const { data: recruitmentStatusOptions } = useEnum("ERecruitmentStatus");
  const { data: genderOptions } = useEnum("EGender");
  const { nationalityOptions } = useGetNationalityList();
  const { specificSkillCertificateOptions } = useGetSpecificSkillCertificateList();
  const { languageTestOptions } = useGetLanguageTestList();

  const handleGenerateResume = async () => {
    if (!defaultValues) return;

    try {
      await generateResumeExcel({
        talentData: defaultValues,
        genderOptions,
        nationalityOptions,
        specificSkillCertificateOptions,
        languageTestOptions,
      });
    } catch (error) {
      showError(error);
    }
  };

  return (
    <Box sx={{ display: "flex", alignItems: "center", mb: 3, width: `${TALENT_WIDTH_FULL}px` }}>
      <Avatar filePath={defaultValues?.avatarFilePath} width={100} height={133} />
      <Box display="flex" alignItems="center" justifyContent="space-between" ml={3} sx={{ flex: 1 }}>
        <Box sx={{ flex: 1 }}>
          <Box sx={{ display: "flex", alignItems: "center", mb: 1, flexWrap: "wrap" }}>
            <Typography
              variant="head20Semi"
              sx={{
                mr: 2,
                whiteSpace: "normal",
                wordBreak: "break-word",
                maxWidth: "60%",
              }}
            >
              {defaultValues?.fullName ? defaultValues.fullName.toUpperCase() : EMPTY_STR.TEXT}
            </Typography>

            <TalentStatusTag
              status={defaultValues?.recruitmentStatus ?? ERecruitmentStatus.DRAFT}
              title={findLabelByValue(recruitmentStatusOptions, defaultValues?.recruitmentStatus)}
              variant="bold"
            />
          </Box>

          <Box display="flex" alignItems="center" gap={1}>
            <Typography variant="body14" color={text.secondary} sx={{ display: "flex", alignItems: "center" }}>
              登録日:
              <span style={{ marginLeft: 4 }}>{defaultValues?.createdAt ? dayjs(defaultValues.createdAt).format(DATE_FORMAT) : ""}</span>
            </Typography>
            <Typography variant="body14" color={text.secondary} sx={{ display: "flex", alignItems: "center" }}>
              |
            </Typography>
            <Typography variant="body14" color={text.secondary} sx={{ display: "flex", alignItems: "center" }}>
              更新日:
              <span style={{ marginLeft: 4 }}>{defaultValues?.updatedAt ? dayjs(defaultValues.updatedAt).format(DATE_FORMAT) : ""}</span>
            </Typography>
          </Box>
        </Box>

        {detailPage ? (
          <Button
            type="button"
            variant="outline"
            size="sm"
            startIcon={<IconAssignment />}
            sx={{ fontWeight: 500, width: "144px" }}
            onClick={handleGenerateResume}
          >
            履歴書を作成
          </Button>
        ) : null}
      </Box>
    </Box>
  );
};
